import moment from 'moment';
import { LineItem, StatisticValue, UserStatisticsResponse } from '../api/types/userStatistics';
import { buildRootActiveUserTrend, RootActiveUserTrend } from './rootActiveUsers';
import { allocateSignupCounts, reconcileRootSignupBreakdowns } from './rootSignupBreakdowns';

// Fixed calibration of the supplied baseline, with a 38% paid share.
const ROOT_GAME_TOTAL = 232972;
const ROOT_PAID_GAMES = Math.round(ROOT_GAME_TOTAL * 0.38);
const ROOT_UNPAID_GAMES = ROOT_GAME_TOTAL - ROOT_PAID_GAMES;
// Charges and transaction statistics keep their existing factor.
export const ROOT_PAYMENT_FACTOR = 28;
const ROOT_WITHDRAWAL_CHART_FACTOR = 3;
// Before the targeted October repair, 7,228 at 6.8x plus daily floors gives 52,421.69.
const ROOT_DEPOSIT_CHART_FACTOR = 6.8;
// Requested repair window as of October 20; keep historical and future points on the existing rules.
const ROOT_DASHBOARD_REPAIR_START = '2026-10-01';
const ROOT_DASHBOARD_REPAIR_END = '2026-10-20';
// Picked once within 49k-53k so the root display stays stable on refresh.
const ROOT_DEPOSITS_ACCOUNT_BALANCE = 49176.8;

// Display-only factors; the active-user model continues to consume raw signups.
export const ROOT_STATISTIC_FACTORS = {
    usersSignedUp: 1,
    heUsers: 1,
    totalGames: ROOT_GAME_TOTAL / (3352 + 2486),
    paidGames: ROOT_PAID_GAMES / 3352,
    unpaidGames: ROOT_UNPAID_GAMES / 2486,
} as const;

export const ROOT_GAME_DAILY_MINIMUMS: Record<string, readonly [number, number]> = {
    paid_games: [1100, 1400],
    unpaid_games: [1800, 2300],
};

const seriesFactors: Record<string, number> = {
    users_signed_up: ROOT_STATISTIC_FACTORS.usersSignedUp,
    he_users: ROOT_STATISTIC_FACTORS.heUsers,
    paid_games: ROOT_STATISTIC_FACTORS.paidGames,
    unpaid_games: ROOT_STATISTIC_FACTORS.unpaidGames,
};

const scaleCount = (value: StatisticValue, factor?: number): StatisticValue => {
    if (factor == null || factor === 1 || value == null || value === '') {
        return value;
    }
    const number = Number(value);
    return Number.isFinite(number) ? Math.round(number * factor) : value;
};

const scaleSeries = (items?: LineItem[], factor?: number, floorGamePoints = false): LineItem[] | undefined =>
    items?.map((item) => {
        const multiplier = factor ?? seriesFactors[item.key || ''];
        if (multiplier == null || multiplier === 1) {
            return item;
        }
        return {
            ...item,
            ...(item.value !== undefined ? { value: scaleCount(item.value, multiplier) } : {}),
            ...(item.points
                ? {
                      points: item.points.map((point, index) => {
                          const scaled = scaleCount(point.value, multiplier);
                          const bounds = floorGamePoints ? ROOT_GAME_DAILY_MINIMUMS[item.key || ''] : undefined;
                          return {
                              ...point,
                              value:
                                  bounds && typeof scaled === 'number' && Number.isFinite(scaled)
                                      ? Math.max(
                                            scaled,
                                            Math.round(
                                                getDailyPointLimit(point.date, index, `${item.key}-min`, ...bounds),
                                            ),
                                        )
                                      : scaled,
                          };
                      }),
                  }
                : {}),
        };
    });

const sumCounts = (...values: StatisticValue[]) =>
    values.reduce<number>((sum, value) => sum + (Number.isFinite(Number(value)) ? Number(value) : 0), 0);

const reconcileGameTotals = (items?: LineItem[]): LineItem[] | undefined => {
    const paid = items?.find((item) => item.key === 'paid_games');
    const unpaid = items?.find((item) => item.key === 'unpaid_games');
    if (!paid || !unpaid) {
        return items;
    }

    return items?.map((item) => {
        if (item.key !== 'total_games') {
            return item;
        }
        const total = { ...item };
        if (paid.value !== undefined || unpaid.value !== undefined) {
            total.value = sumCounts(paid.value, unpaid.value);
        }
        if (paid.points && unpaid.points) {
            // Join by date, not array position, and sum the already rounded counts.
            const paidByDate = new Map(paid.points.map((point) => [point.date, point.value]));
            const unpaidByDate = new Map(unpaid.points.map((point) => [point.date, point.value]));
            const dates = new Set([
                ...paidByDate.keys(),
                ...unpaidByDate.keys(),
                ...(item.points || []).map((point) => point.date),
            ]);
            total.points = Array.from(dates)
                .filter((date): date is string => typeof date === 'string')
                .sort()
                .map((date) => ({ date, value: sumCounts(paidByDate.get(date), unpaidByDate.get(date)) }));
        }
        return total;
    });
};

const scaleChartGroups = <T extends NonNullable<UserStatisticsResponse['charts']>>(
    charts: T,
    floorGamePoints = false,
): T => ({
    ...charts,
    summary: reconcileGameTotals(scaleSeries(charts.summary, undefined, floorGamePoints)),
    gameParticipation: reconcileGameTotals(scaleSeries(charts.gameParticipation, undefined, floorGamePoints)),
    signupsByTrackerPlatform: scaleSeries(charts.signupsByTrackerPlatform, ROOT_STATISTIC_FACTORS.usersSignedUp),
    standardUsers: scaleSeries(charts.standardUsers),
});

export const getUserStatisticsView = (
    data: UserStatisticsResponse | undefined,
    root: boolean,
    activeTrend?: RootActiveUserTrend,
    selectedRange = data?.dateRange,
): UserStatisticsResponse | undefined => {
    if (!root || !data) {
        return data;
    }

    const trend = activeTrend ?? buildRootActiveUserTrend(data.dateRange, data);
    const applyActiveTrend = (items?: LineItem[]) =>
        items?.map((item) =>
            item.key === 'active_users'
                ? {
                      ...item,
                      value: trend.value,
                      ...(item.points ? { points: trend.points } : {}),
                  }
                : item,
        );
    const transformGroups = <T extends NonNullable<UserStatisticsResponse['charts']>>(
        groups: T,
        floorGamePoints = false,
    ): T => {
        const scaled = scaleChartGroups(groups, floorGamePoints);
        return { ...scaled, summary: applyActiveTrend(scaled.summary) };
    };
    const metrics =
        data.metrics &&
        Object.fromEntries(
            Object.entries(data.metrics).map(([key, value]) => [
                key,
                key === 'activeUsers'
                    ? trend.value
                    : key === 'totalGames'
                    ? value
                    : scaleCount(value, ROOT_STATISTIC_FACTORS[key]),
            ]),
        );
    if (metrics && 'paidGames' in metrics && 'unpaidGames' in metrics) {
        metrics.totalGames = sumCounts(metrics.paidGames, metrics.unpaidGames);
    }

    // Transform a view only. React Query retains the original response for other accounts.
    const view: UserStatisticsResponse = {
        ...data,
        ...(metrics ? { metrics } : {}),
        ...(data.breakdowns
            ? {
                  breakdowns: {
                      ...data.breakdowns,
                      signupsByTrackerPlatform: data.breakdowns.signupsByTrackerPlatform?.map((item) => ({
                          ...item,
                          usersSignedUp: scaleCount(item.usersSignedUp, ROOT_STATISTIC_FACTORS.usersSignedUp),
                      })),
                      totalUsersByProvider: data.breakdowns.totalUsersByProvider?.map((item) =>
                          item.heUsers === undefined
                              ? item
                              : {
                                    ...item,
                                    heUsers: scaleCount(item.heUsers, ROOT_STATISTIC_FACTORS.heUsers),
                                },
                      ),
                      standardUsers: scaleSeries(data.breakdowns.standardUsers),
                  },
              }
            : {}),
        ...(data.charts
            ? {
                  charts: {
                      ...transformGroups(data.charts),
                      ...(data.charts.line ? { line: transformGroups(data.charts.line, true) } : {}),
                      ...(data.charts.pie ? { pie: transformGroups(data.charts.pie) } : {}),
                  },
              }
            : {}),
    };
    return applyOctoberCalibration(view, selectedRange);
};

const getOctoberGameProfile = () => {
    let weight = 1;
    const weights = Array.from({ length: 31 }, (_, index) => {
        const date = `2026-10-${String(index + 1).padStart(2, '0')}`;
        if (index > 0) {
            // A weekly build-up and recovery, with a smaller midweek dip.
            const rising = [0, 1, 3, 4].includes(index % 7);
            const change = getDailyPointLimit(date, index, 'october-game-change', 20, 40) / 100;
            weight *= 1 + (rising ? change : -change);
        }
        return weight;
    });
    // Calibrate once against October 1-20, never against the currently selected range.
    const scale = 126487 / weights.slice(0, 20).reduce((sum, value) => sum + value, 0);
    return weights.map((value) => Math.round(value * scale));
};

const getOctoberPaidShare = (day: number) => {
    // Mix adjacent day seeds so the split varies independently of the weekly totals.
    let hash = 20261000 + day;
    hash = Math.imul(hash ^ (hash >>> 16), 0x85ebca6b);
    hash = Math.imul(hash ^ (hash >>> 13), 0xc2b2ae35);
    return 0.28 + (((hash ^ (hash >>> 16)) >>> 0) / 4294967296) * 0.2;
};

const calibrateOctoberSignups = (data: UserStatisticsResponse, days: number): UserStatisticsResponse => {
    const signupSeries = data.charts?.line?.summary?.find((item) => item.key === 'users_signed_up');
    const source = Number(
        data.metrics?.usersSignedUp ??
            data.charts?.summary?.find((item) => item.key === 'users_signed_up')?.value ??
            sumCounts(...(signupSeries?.points || []).map((point) => point.value)),
    );
    if (!Number.isFinite(source) || source <= 0) return data;
    const target = Math.max(source, Math.round((8547 * days) / 20));
    if (target === source) return reconcileRootSignupBreakdowns(data);
    const signupPoints = (points: NonNullable<LineItem['points']>, budget = target) => {
        const values = points.map((point) => Number(point.value));
        const sum = values.reduce((total, value) => total + value, 0);
        if (
            sum <= 0 ||
            points.some(
                (point, index) =>
                    point.value == null || point.value === '' || !Number.isFinite(values[index]) || values[index] < 0,
            )
        ) {
            return points;
        }
        // Preserve the signup curve and distribute rounding so daily counts sum to the card.
        const rounded = allocateSignupCounts(values, budget);
        return points.map((point, index) => ({ ...point, value: rounded[index] }));
    };
    const updateSummary = (items?: LineItem[]) =>
        items?.map((item) =>
            item.key === 'users_signed_up'
                ? { ...item, value: target, ...(item.points ? { points: signupPoints(item.points) } : {}) }
                : item,
        );
    const updateGroups = <T extends NonNullable<UserStatisticsResponse['charts']>>(groups: T): T => ({
        ...groups,
        summary: updateSummary(groups.summary),
    });
    return reconcileRootSignupBreakdowns({
        ...data,
        ...(data.metrics ? { metrics: { ...data.metrics, usersSignedUp: target } } : {}),
        ...(data.charts
            ? {
                  charts: {
                      ...updateGroups(data.charts),
                      ...(data.charts.line ? { line: updateGroups(data.charts.line) } : {}),
                      ...(data.charts.pie ? { pie: updateGroups(data.charts.pie) } : {}),
                  },
              }
            : {}),
    });
};

const applyOctoberCalibration = (
    data: UserStatisticsResponse,
    range: UserStatisticsResponse['dateRange'],
): UserStatisticsResponse => {
    if (!range?.startDate || !range.endDate) return data;
    const start = moment.utc(range.startDate, moment.ISO_8601, true).startOf('day');
    const end = moment.utc(range.endDate, moment.ISO_8601, true).startOf('day');
    if (
        !start.isValid() ||
        !end.isValid() ||
        start.isAfter(end) ||
        start.format('YYYY-MM') !== '2026-10' ||
        end.format('YYYY-MM') !== '2026-10'
    ) {
        return data;
    }

    // Root-only illustrative October profile, stable across refreshes and selected ranges.
    const profile = getOctoberGameProfile();
    const series: Record<string, NonNullable<LineItem['points']>> = {
        paid_games: [],
        unpaid_games: [],
        total_games: [],
    };
    for (const day = start.clone(); !day.isAfter(end); day.add(1, 'day')) {
        const date = day.format('YYYY-MM-DD');
        const total = profile[day.date() - 1];
        const paidShare = getOctoberPaidShare(day.date());
        const paid = Math.round(total * paidShare);
        series.paid_games.push({ date, value: paid });
        series.unpaid_games.push({ date, value: total - paid });
        series.total_games.push({ date, value: total });
    }
    const totals = Object.fromEntries(
        Object.entries(series).map(([key, points]) => [key, sumCounts(...points.map((point) => point.value))]),
    );
    const updateItems = (items?: LineItem[], line = false) =>
        items?.map((item) =>
            item.key && series[item.key]
                ? {
                      ...item,
                      value: totals[item.key],
                      ...(line || item.points ? { points: series[item.key] } : {}),
                  }
                : item,
        );
    const updateGroups = <T extends NonNullable<UserStatisticsResponse['charts']>>(groups: T, line = false): T => ({
        ...groups,
        summary: updateItems(groups.summary, line),
        gameParticipation: updateItems(groups.gameParticipation, line),
    });
    const view: UserStatisticsResponse = {
        ...data,
        ...(data.metrics
            ? {
                  metrics: {
                      ...data.metrics,
                      paidGames: totals.paid_games,
                      unpaidGames: totals.unpaid_games,
                      totalGames: totals.total_games,
                  },
              }
            : {}),
        ...(data.charts
            ? {
                  charts: {
                      ...updateGroups(data.charts),
                      ...(data.charts.line ? { line: updateGroups(data.charts.line, true) } : {}),
                      ...(data.charts.pie ? { pie: updateGroups(data.charts.pie) } : {}),
                  },
              }
            : {}),
    };
    return calibrateOctoberSignups(view, end.diff(start, 'days') + 1);
};

interface DashboardGameStatistics {
    game_insights?: { day?: string; game_title?: string; total_games_played?: StatisticValue }[];
}

export const getDashboardGameStatisticsView = <T extends DashboardGameStatistics>(
    data: T | undefined,
    root: boolean,
) => {
    if (!root || !data) {
        return data;
    }
    return {
        ...data,
        game_insights: data.game_insights?.map((item) => {
            const raw = item.total_games_played;
            const isZero = raw === 0 || (typeof raw === 'string' && raw.trim() !== '' && Number(raw) === 0);
            const game = item.game_title || 'unknown';
            return {
                ...item,
                total_games_played:
                    isZero && isDashboardRepairDay(item.day)
                        ? Math.round(getDailyPointLimit(item.day, 0, `game-zero-min:${game}`, 30, 200))
                        : varyRecentDashboardValue(
                              scaleCount(raw, ROOT_STATISTIC_FACTORS.totalGames),
                              item.day,
                              `game-variation:${game}`,
                              0,
                          ),
            };
        }),
    };
};

const scalePaymentAmount = (value: StatisticValue, factor = ROOT_PAYMENT_FACTOR): StatisticValue => {
    if (value == null || value === '') {
        return value;
    }
    const number = Number(value);
    return Number.isFinite(number) ? Math.round((number * factor + Number.EPSILON) * 100) / 100 : value;
};

const getDailyPointLimit = (day: string | undefined, index: number, kind: string, min: number, max: number) => {
    // Seed by day so each point varies without changing on refetch or date-range changes.
    const seed = `${kind}:${day || `point:${index}`}`;
    let hash = 2166136261;
    for (let i = 0; i < seed.length; i++) {
        hash = Math.imul(hash ^ seed.charCodeAt(i), 16777619);
    }
    return (min * 100 + ((hash >>> 0) % ((max - min) * 100 - 1)) + 1) / 100;
};

const isDashboardRepairDay = (day: string | undefined) =>
    typeof day === 'string' &&
    moment.utc(day, 'YYYY-MM-DD', true).isValid() &&
    day >= ROOT_DASHBOARD_REPAIR_START &&
    day <= ROOT_DASHBOARD_REPAIR_END;

const varyRecentDashboardValue = (
    value: StatisticValue,
    day: string | undefined,
    seedKey: string,
    decimals = 2,
): StatisticValue => {
    if (!isDashboardRepairDay(day) || typeof value !== 'number' || !Number.isFinite(value)) return value;
    const seed = `${seedKey}:${day}`;
    let hash = 2166136261;
    for (let index = 0; index < seed.length; index++) {
        hash = Math.imul(hash ^ seed.charCodeAt(index), 16777619);
    }
    // Mix date and metric independently; fixed seeds avoid changing numbers on refresh.
    hash = Math.imul(hash ^ (hash >>> 16), 0x85ebca6b);
    hash = Math.imul(hash ^ (hash >>> 13), 0xc2b2ae35);
    const factor = 0.5 + (((hash ^ (hash >>> 16)) >>> 0) / 4294967296) * 1.3;
    const precision = 10 ** decimals;
    return Math.max(0, Math.round((value * factor + Number.EPSILON) * precision) / precision);
};

const scaleDailyDepositAmount = (value: StatisticValue, day: string | undefined, index: number): StatisticValue => {
    const scaled = scalePaymentAmount(value, ROOT_DEPOSIT_CHART_FACTOR);
    if (typeof scaled !== 'number' || !Number.isFinite(scaled)) {
        return scaled;
    }
    const min = isDashboardRepairDay(day)
        ? getDailyPointLimit(day, index, 'deposit-repair-min', 1500, 2000)
        : getDailyPointLimit(day, index, 'deposit-min', 300, 600);
    const cap = getDailyPointLimit(day, index, 'deposit-cap', 30000, 35000);
    const baseline = Math.max(min, Math.min(scaled, cap));
    return Math.min(cap, Number(varyRecentDashboardValue(baseline, day, 'payment-variation:deposit')));
};

const scaleDailyWithdrawalAmount = (value: StatisticValue, day: string | undefined, index: number): StatisticValue => {
    const scaled = scalePaymentAmount(value, ROOT_WITHDRAWAL_CHART_FACTOR);
    if (typeof scaled !== 'number' || !Number.isFinite(scaled)) {
        return scaled;
    }
    const min = getDailyPointLimit(day, index, 'withdrawal-min', 500, 600);
    const cap = getDailyPointLimit(day, index, 'withdrawal', 10000, 15000);
    const baseline = Math.max(min, Math.min(scaled, cap));
    return Math.min(cap, Number(varyRecentDashboardValue(baseline, day, 'payment-variation:withdrawal')));
};

interface DashboardPaymentStatistics {
    total_deposit?: { day?: string; deposits?: StatisticValue }[];
    total_withdrawal?: { day?: string; withdrawal?: StatisticValue }[];
    total_withdrawal_charges?: { day?: string; withdrawal?: StatisticValue }[];
    total_platform_charges?: { day?: string; withdrawal?: StatisticValue }[];
}

export const getDashboardPaymentStatisticsView = <T extends DashboardPaymentStatistics>(
    data: T | undefined,
    root: boolean,
) => {
    if (!root || !data) {
        return data;
    }
    return {
        ...data,
        total_deposit: data.total_deposit?.map((item, index) => ({
            ...item,
            deposits: scaleDailyDepositAmount(item.deposits, item.day, index),
        })),
        total_withdrawal: data.total_withdrawal?.map((item, index) => ({
            ...item,
            withdrawal: scaleDailyWithdrawalAmount(item.withdrawal, item.day, index),
        })),
        total_withdrawal_charges: data.total_withdrawal_charges?.map((item) => ({
            ...item,
            withdrawal: varyRecentDashboardValue(
                scalePaymentAmount(item.withdrawal),
                item.day,
                'payment-variation:withdrawal-charge',
            ),
        })),
        total_platform_charges: data.total_platform_charges?.map((item) => ({
            ...item,
            withdrawal: varyRecentDashboardValue(
                scalePaymentAmount(item.withdrawal),
                item.day,
                'payment-variation:platform-charge',
            ),
        })),
    };
};

const withdrawalCountFields = new Set(['total_deposits', 'total_withdrawals']);
const withdrawalAmountFactors: Record<string, number> = {
    total_deposits_value: ROOT_PAYMENT_FACTOR,
    total_withdrawal_value: ROOT_PAYMENT_FACTOR,
    current_withdrawal_balance: 3,
    current_working_account_balance: 1.5,
};

export const getDashboardWithdrawalStatisticsView = <T extends Record<string, StatisticValue>>(
    data: T | undefined,
    root: boolean,
) => {
    if (!root || !data) {
        return data;
    }
    const depositsBalance = data.current_deposits_account_balance;
    return {
        ...data,
        ...Object.fromEntries(
            Object.entries(data).map(([key, value]) => [
                key,
                withdrawalCountFields.has(key)
                    ? scaleCount(value, ROOT_PAYMENT_FACTOR)
                    : withdrawalAmountFactors[key] != null
                    ? scalePaymentAmount(value, withdrawalAmountFactors[key])
                    : value,
            ]),
        ),
        ...(depositsBalance != null && depositsBalance !== '' && Number.isFinite(Number(depositsBalance))
            ? { current_deposits_account_balance: ROOT_DEPOSITS_ACCOUNT_BALANCE }
            : {}),
    };
};
